//============================================================================
// Name        : receiveActions.ts
// Purpose     : Define store Receive actions
// Created     : 2019-07-26
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

import Moment from "moment";
import { extendMoment } from "moment-range";

import Lot from "../../../../manufacturing/Lot";
import {ICatalogItem, IPrintLabelItem, IReceiveProps, IReceptionItem, IStorageUnit, ReceiveActions, ReceiveStatuses} from "../types";

// Add Ranges plugin for MomentJS
const moment = extendMoment(Moment);

// Define base Action interface
export interface IReceptionAction {
    type: ReceiveActions;
    payload: object;
}

export function checkLotValid(lot: Lot): boolean {
    if (lot.receiveQty != "0") {
        return true;
    }
    else {
        return false;
    }
}

export function checkLotsValid(lots: Lot[]): boolean {
    return false;
}

//================================ Actions ===================================

//============================================================================
// Show the error dialog
//============================================================================
export interface IShowErrorDialogAction extends IReceptionAction {
    payload: {
        errorTitle: string;
        errorMsg: string;
    };
}
export function showErrorDialog(state: Partial<IReceiveProps>, action: IShowErrorDialogAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showError: true,
        errorTitle: action.payload.errorTitle,
        errorMsg: action.payload.errorMsg
    };
}

//============================================================================
// Hide the error dialog
//============================================================================
export function hideErrorDialog(state: Partial<IReceiveProps>): IReceiveProps {
    if (state.dismissErrorCallback) {
        state.dismissErrorCallback();
    }
    return {
        ...state as IReceiveProps,
        showError: false
    };
}

//============================================================================
// Set callback method for the error dialog dismiss action
//============================================================================
export interface ISetErrorDismissCallbackAction extends IReceptionAction {
    payload: { callback: () => void };
}
export function setErrorDismissCallback(state: Partial<IReceiveProps>, action: ISetErrorDismissCallbackAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        dismissErrorCallback: action.payload.callback
    };
}

//============================================================================
// Updates the store state with given values
//============================================================================
export interface IUpdateStateAction extends IReceptionAction {
    payload: IReceiveProps;
}
export function updateState(state: Partial<IReceiveProps>, action: IUpdateStateAction): IReceiveProps {
    return {
        ...state,
        ...action.payload
    };
}

//============================================================================
// Validate that reception dates are correct and continue with the reception
//============================================================================
export function continueReception(state: Partial<IReceiveProps>): IReceiveProps {
    // Check that the reception date is in the valid range allowed
    let validDates = false;

    const properAllowedStart = moment(state.allowedStartDate, "YYYY-MM-DD");
    const properAllowedEnd   = moment(state.allowedEndDate, "YYYY-MM-DD");

    const rangeActualDate = moment().range(properAllowedStart, properAllowedEnd);
    const rangePostingDate = moment().range(properAllowedStart, properAllowedEnd);

    if (rangePostingDate.contains(moment(state.postingDate, "YYYY-MM-DD"))) {
        validDates = true;
        return {
            ...state as IReceiveProps,
            isReceiving: true,
            validDates
        };
    } else {
        validDates = false;
        return {
            ...state as IReceiveProps,
            showError: true,
            errorTitle: "Invalid date range",
            errorMsg: "Invalid date range detected!",
            isReceiving: false,
            validDates
        };
    }
}

//============================================================================
// Begin reception
//============================================================================
export interface IBeginReceptionAction extends IReceptionAction {
    payload: { id: number };
}
export function beginReception(state: Partial<IReceiveProps>, action: IBeginReceptionAction): IReceiveProps {
    // Change the properties on the reception items
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idxItem) => {
        if (item.id == action.payload.id) {
            item.blocked = false;
            item.isReceiving = true;
            item.status = ReceiveStatuses.InProgress;
        }
        else {
            item.isReceiving = false;
        }

        if (!item.lots) {
            const lot = new Lot(0);
            lot.lotNumber = state.postingDate;
            item.lots = [lot];
        }
        if (!item.labels) {
            item.labels = [];
        }
        return item;
    });

    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Cancel  the reception
//============================================================================
export function cancelReception(state: Partial<IReceiveProps>): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        item.blocked = false;
        item.isReceiving = false;
        const lot = new Lot(0);
        lot.lotNumber = state.postingDate;
        item.lots = [lot];
        return item;
    });

    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        showConfirmation: false,
        isReceiving: false,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Show the cancel reception dialog
//============================================================================
export interface IShowCancelConfirmationAction extends IReceptionAction {
    payload: {
        positiveCb: () => void;
        negativeCb: () => void;
    };
}
export function showCancelConfirmation(state: Partial<IReceiveProps>, action: IShowCancelConfirmationAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showConfirmation: true,
        confirmationTitle: "Confirmation",
        confirmationMsg: "Are you sure you want to cancel receiving this order?",
        confirmationDlgPositiveCallback: action.payload.positiveCb,
        confirmationDlgNegativeCallback: action.payload.negativeCb
    };
}

//============================================================================
// Show the cancel receiving item confirmation dialog
//============================================================================
export interface IShowCancelItemConfirmationAction extends IReceptionAction {
    payload: {
        positiveCb: () => void,
        negativeCb: () => void
    };
}
export function showCancelItemConfirmation(state: Partial<IReceiveProps>, action: IShowCancelItemConfirmationAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showItemConfirmation: true,
        confirmationTitle: "Confirmation",
        confirmationMsg: "Are you sure you want to cancel receiving this item?",
        confirmationDlgPositiveCallback: action.payload.positiveCb,
        confirmationDlgNegativeCallback: action.payload.negativeCb
    };
}

//============================================================================
// Show the cancel reception dialog
//============================================================================
export interface IShowChangeDateConfirmationAction extends  IReceptionAction {
    payload: {
        positiveCb: () => void;
        negativeCb: () => void;
    };
}
export function showChangeDateConfirmation(state: Partial<IReceiveProps>, action: IShowChangeDateConfirmationAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showConfirmation: true,
        confirmationTitle: "Confirmation",
        confirmationMsg: "PLEASE CONFIRM DATE CHANGE",
        confirmationDlgPositiveCallback: action.payload.positiveCb,
        confirmationDlgNegativeCallback: action.payload.negativeCb
    };
}

//============================================================================
// Show undo item reception confirmation dialog
//============================================================================
export interface IShowUndoItemConfirmationAction extends IReceptionAction {
    payload: {
        callback: () => void;
        negativeCb: () => void;
    };
}
export function showUndoItemConfirmation(state: Partial<IReceiveProps>, action: IShowUndoItemConfirmationAction): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showConfirmation: true,
        confirmationTitle: "Confirmation",
        confirmationMsg: "Are you sure you want to undo receiving this item?",
        confirmationDlgPositiveCallback: action.payload.callback,
        confirmationDlgNegativeCallback: action.payload.negativeCb
    };
}

//============================================================================
// Hide cancel confirmation dialog
//============================================================================
export function hideCancelConfirmation(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showConfirmation: false
    };
}

//============================================================================
// Hide the cancel item confirmation dialog
//============================================================================
export function hideCancelItemConfirmation(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showItemConfirmation: false
    };
}

//============================================================================
// Cancel receiving an item
//============================================================================
export interface ICancelReceiveAction extends IReceptionAction {
    payload: {
        itemId: number;
    };
}
export function cancelReceiveItem(state: Partial<IReceiveProps>, action: ICancelReceiveAction): IReceiveProps {
    // Change the properties on the reception items
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        item.blocked = false;
        item.isReceiving = false;
        if (item.id == action.payload.itemId) {
            item.status = ReceiveStatuses.Pending;
            const lot = new Lot(0);
            lot.lotNumber = state.postingDate;
            item.lots = [lot];
        }
        return item;
    });

    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        showItemConfirmation: false,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Retrieve catalog item info for a receiving item
//============================================================================
export interface IFetchItemInfoAction extends IReceptionAction {
    payload: {
        itemNumber: string;
        catalogItem: ICatalogItem;
    };
}
export function fetchItemInfo(state: Partial<IReceiveProps>, action: IFetchItemInfoAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        if (item.item_number == action.payload.itemNumber) {
            item.catalogItem = action.payload.catalogItem;
        }
        return item;
    });
    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Add a lot to the receiving item
//============================================================================
export interface IAddLotAction extends IReceptionAction {
    payload: {
        itemId: number;
        lotId: number;
        newState: IReceiveProps;
    };
}
export function addLot(state: Partial<IReceiveProps>, action: IAddLotAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idxItem) => {
        if (item.id == action.payload.itemId) {
            const lot = new Lot(item.lots.length);
            lot.lotNumber = state.postingDate;
            item.lots.push(lot);
        }
        return item;
    });
    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Populate the storage units for the receiving item
//============================================================================
export interface ISetItemStorageUnitsAction extends IReceptionAction {
    payload: {
        itemNumber: string;
        storageUnits: IStorageUnit[];
        defaultStorageUnit: IStorageUnit;
    };
}
export function setItemStorageUnits(state: Partial<IReceiveProps>, action: ISetItemStorageUnitsAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idxItem) => {
        if (item.item_number == action.payload.itemNumber) {
            item.storageUnits = action.payload.storageUnits;
            item.defaultStorageUnit = action.payload.defaultStorageUnit;
        }
        return item;
    });
    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Update a lot and perform the math validation for the receiving quantities
//============================================================================
export interface IUpdateLotAction extends IReceptionAction {
    payload: {
        itemId: number;
        lotId: number;
        newState: IReceiveProps;
    };
}
function getPercentage(oldNumber: number, newNumber: number): number {
    const decVal = oldNumber - newNumber;
    return (decVal / oldNumber) * 100;
}
export function updateLot(state: Partial<IReceiveProps>, action: IUpdateLotAction): IReceiveProps {
    let errorMsg = state.errorMsg;

    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idxItem) => {
        if (item.id == action.payload.itemId && item.defaultStorageUnit) {
            let allLotsValid = false;
            let lotsQty = 0;

            // Sum up the quantities in all lots
            item.lots.map((lot: Lot, _idxLot) => {
                lotsQty += lot.getQtyInPounds(item.storageUnits, item.defaultStorageUnit);
            });

            // Check if the qty in all lots matches the expected reception qty
            // and that it's within the qty tolerance percentage
            const storageUnitL = item.storageUnits.find(x => x.name == item.line_uofm_code);
            const expQty = item.expected_qty * parseFloat(storageUnitL.measure_unit);

            console.log(`All lots Qty: ${lotsQty}`);
            console.log(`Expected Qty: ${expQty}`);
            console.log(`Qty Tollerance: ${state.qtyTolerance}`);
            console.log(`Qty Tollerance Under: ${state.qtyToleranceUnder}`);

            // const percentOfQty = getPercentage(lotsQty, expQty);
            let percentOfQty = (lotsQty / expQty) * 100;
            percentOfQty = percentOfQty >= 100 ? percentOfQty - 100 : percentOfQty;
            console.log(`Percent of qty: ${Math.abs(percentOfQty)}`);

            if ( (lotsQty >= expQty && percentOfQty <= state.qtyTolerance) ||
                 (lotsQty <= expQty && (100 - percentOfQty) <= state.qtyToleranceUnder) ) {
                allLotsValid = true;
            }
            else {
                allLotsValid = false;
                errorMsg += "\n * CANNOT PROCEED *, QTY OVER / UNDER ALLOWABLE RECEIPT %\nPLEASE CONTACT LAURA FOR CORRECTION @ EXT 104";
            }

            // Update all lots with the validation status
            const lots = item.lots.map((lot: Lot, _idxLot) => {
                if (lot.id == action.payload.lotId) {
                    if ((action.payload.newState as any).hasCustomStorageUnit) {
                        lot.customStorageUnit = item.defaultStorageUnit.measure_unit;
                    }
                    else {
                        if ((action.payload.newState as any).customStorageUnit) {
                            lot.customStorageUnit = (action.payload.newState as any).customStorageUnit;
                        }
                    }
                }
                console.log(`Custom Storage Unit: ${lot.customStorageUnit}`);

                const expectedWeight = Math.floor( lot.getQtyInPounds(item.storageUnits,  item.defaultStorageUnit) );
                const receiveQtyLbs  = Math.floor( lot.getRecieveQtyLBS(item.storageUnits) );
                let lotValid = (receiveQtyLbs == expectedWeight) && receiveQtyLbs > 0;

                if (!lotValid) {
                    console.log("Weight measure calculation valid.");
                    console.log(`Receive Qty: ${receiveQtyLbs} != ${expectedWeight}`);
                    errorMsg += "\n * CANNOT PROCEED *, TOTAL RECEIVED QUANTITY DOES NOT EQUAL # OF PALLETS MULTIPLIED BY # OF CASES PER PALLET.\n ------";

                    const caseCount = item.lots.reduce((acc, accLot: Lot) =>
                        acc + (parseInt(accLot.palletCases, 10) * parseInt(accLot.palletCount, 10) + parseInt(accLot.individualCases, 10)), 0);

                    errorMsg += `\nTotal Number of Cases: ${caseCount}`;
                    errorMsg += `\nInput Qty: ${parseInt(lot.receiveQty, 10)}`;
                    errorMsg += `\nInput Weight: ${receiveQtyLbs} != ${expectedWeight} Weight of Cases`;
                }

                if (lot.id == action.payload.lotId) {
                    const newLot = new Lot(lot.id);
                    newLot.loadFromLot(lot);
                    newLot.setFromState(action.payload.newState);

                    console.log(`Lot valid before date check: ${lotValid}`);
                    lotValid = lotValid && (newLot.expirationDate != "") && !newLot.expirationDate.includes("NaN") && !newLot.expirationDate.includes("---") && moment(newLot.expirationDate, "YYYY-MM-DD").isValid();

                    if (!(newLot.expirationDate != "") && !newLot.expirationDate.includes("NaN")) {
                        errorMsg += "\n Please Enter Expiration Date";
                        console.log(`Expiration date for lot invalid: ${newLot.expirationDate}`);
                    }

                    if (!allLotsValid) {
                        console.log(errorMsg);
                    }

                    newLot.isValid = allLotsValid && lotValid;
                    allLotsValid = allLotsValid && lotValid;
                    return newLot;
                }
                if (lot.isValid != allLotsValid) {
                    const newLot = new Lot(lot.id);
                    newLot.loadFromLot(lot);
                    lotValid = lotValid && (newLot.expirationDate != "");

                    newLot.isValid = allLotsValid && lotValid;
                    return newLot;
                }

                allLotsValid = (allLotsValid && lotValid);
                return lot;
            });
            item.lots = lots;
            item.qtyValid = allLotsValid;

        }
        return item;
    });

    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        errorMsg,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Handle removing a lot from the receiving item
//============================================================================
export interface IRemoveLotAction extends IReceptionAction {
    payload: {
        itemId: number;
        lotId: number;
    };
}
export function removeLot(state: Partial<IReceiveProps>, action: IRemoveLotAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idxItem) => {
        if (item.id == action.payload.itemId) {
            const lots = item.lots.filter(l => l.id != action.payload.lotId);
            item.lots = lots;
        }
        return item;
    });

    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Show the print dialog
//============================================================================
export function printLabels(state: Partial<IReceiveProps>): IReceiveProps {
    $(".receive-overlay-wrapper").fadeIn();
    return {
        ...state as IReceiveProps,
        showPrintDialog: true
    };
}

//============================================================================
// Show label reprint dialog
//============================================================================
export function showReprintDialog(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showReprintDialog: true
    };
}

//============================================================================
// hide label reprint dialog
//============================================================================
export function hideReprintDialog(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showReprintDialog: false
    };
}

//============================================================================
// Show a confirmation dialog for cancelling printing
//============================================================================
export function confirmCancelPrint(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showCancelPrintConfirm: true,
        confirmationTitle: "Confirmation",
        confirmationMsg: "Are you sure you want to cancel printing labels?"
    };
}

//============================================================================
// Hide the cancel print dialog
//============================================================================
export function hideCancelPrint(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showCancelPrintConfirm: false
    };
}

//============================================================================
// Cancel the label printing
//============================================================================
export function cancelPrinting(state: Partial<IReceiveProps>): IReceiveProps {
    return {
        ...state as IReceiveProps,
        showCancelPrintConfirm: false,
        showPrintDialog: false
    };
}

//============================================================================
// Save the generated labels for the receiving item into the store
//============================================================================
export interface ISaveGeneratedLabelsAction extends IReceptionAction {
    payload: {
        itemId: number;
        labels: IPrintLabelItem[];
    };
}
export function saveGeneratedLabels(state: Partial<IReceiveProps>, action: ISaveGeneratedLabelsAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        if (item.id == action.payload.itemId) {
            item.labels = action.payload.labels;
        }
        return item;
    });
    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Mark a generated label as read by the backend and thus verified
//============================================================================
export interface ISetLabelVerifiedAction extends IReceptionAction {
    payload: {
        itemId: number;
        epc: string;
    };
}
export function setLabelVerified(state: Partial<IReceiveProps>, action: ISetLabelVerifiedAction): IReceiveProps {
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        if (item.id == action.payload.itemId) {
            const updatedLabels = item.labels.map(label => {
                const newLabel = { ...label };
                if (label.epc == action.payload.epc) {
                    newLabel.verified = true;
                }
                return newLabel;
            });
            item.labels = updatedLabels;
        }
        return item;
    });
    reception.external_receive_items = updatedItems;
    return {
        ...state as IReceiveProps,
        reception: {
            ...state.reception,
            external_receive_items: updatedItems
        }
    };
}

//============================================================================
// Upadate the tags on the backend into live status
//============================================================================
export interface IFinishReceivingItemAction extends IReceptionAction {
    payload: { id: number; };
}
export function finishReceivingItem(state: Partial<IReceiveProps>, action: IFinishReceivingItemAction) {
    // Once we have updated everything on the backend, proceed to mark the reciving item as completed
    let allItemsComplete = true;
    const reception = state.reception;
    const updatedItems = state.reception.external_receive_items.map((item, _idx) => {
        if (item.id == action.payload.id) {
            item.status = ReceiveStatuses.Complete;
            item.isReceiving = false;
        }
        if (item.status != ReceiveStatuses.Complete) {
            allItemsComplete = false;
        }
        return item;
    });
    reception.external_receive_items = updatedItems;

    // If all the items for this reception are received mark it as complete
    // the backend will do the same to persist it.
    if (allItemsComplete) {
        reception.isReceiving = false;
        reception.status = ReceiveStatuses.Complete;
    }

    $(".receive-overlay-wrapper").fadeOut();
    return {
        ...state as IReceiveProps,
        reception: {
            ...reception,
            external_receive_items: updatedItems
        },
        showPrintDialog: false
    };
}

//============================================================================
// Mark the reception status as complete on the UI and backend
//============================================================================
export interface IFinishReceptionAction extends IReceptionAction {
    payload: {
        apiKey: string;
        apiUrl: string;
    };
}
export function finishReception(state: Partial<IReceiveProps>, action: IFinishReceptionAction): IReceiveProps {
    state.isReceiving = false;
    state.reception.isReceiving = false;
    state.reception.status = ReceiveStatuses.Complete;
    return {
        ...state as IReceiveProps
    };
}

//==============================================================================
//
//==============================================================================
export interface IShowUndoProcessAction extends IReceptionAction {}
export function showUndoProcess(state: Partial<IReceiveProps>, action: IShowUndoProcessAction): IReceiveProps {
    return {
      ...state as IReceiveProps,
        showUndoProcess: true
    };
}
